import { User } from '@overtur-monorepo/keying-user'
import { Component, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatDialog } from '@angular/material/dialog'
import { Router, ActivatedRoute } from '@angular/router'
import { BehaviorSubject, forkJoin, fromEvent, Subject, interval, Subscription } from 'rxjs'
import moment from 'moment'
import { NavService } from '../../../services/nav.service'
import { fadeIn } from '../../../animations/fadeIn'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { keyingDisplayColumns } from '../../../constants/keying.constants'
import { KeySystemStatuses } from '../../../constants/keySystem-statuses'
import { NewStructureDialogComponent } from '../../../dialogs/new-structure-dialog/new-structure-dialog.component'
import { debounceTime, distinctUntilChanged, takeUntil, tap, take, switchMap } from 'rxjs/operators'
import { Library, Structure } from '../../../models/dev.test.models'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { MasterKeyService } from '../../../services/keying.masterkey.service'
import { StructureSearchParamsService } from '../../../services/structure-search-params.service'
import { ErrorService } from '../../../services/error.service'
import { StructureSearchParams } from '../../../models/structures-search-params'
import { Loading, LoadingService } from '../../../services/loading.service'
import { DialogService } from '../../../services/dialog.service'
import { Clipboard } from '@angular/cdk/clipboard'
import { Accept, ReportsService } from '../../../services/reports.service'
import { KeyingDataSets, KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { ToastService } from '@overtur-monorepo/keying-shared'
import { ReportsDialogComponent } from '../../../dialogs/reports-dialog/reports-dialog.component'

@Component({
  selector: 'app-find-key-system',
  templateUrl: './find-key-system.component.html',
  styleUrls: ['./find-key-system.component.scss'],
  animations: [fadeIn],
  standalone: false,
})
export class FindKeySystemComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort
  @ViewChild('jobName') jobName: ElementRef
  @ViewChild('structureNumber') structureNumber: ElementRef
  @ViewChild('city') city: ElementRef
  @ViewChild('state') state: ElementRef
  @ViewChild('country') country: ElementRef
  @ViewChild('topMasterKeyName') topMasterKeyName: ElementRef
  @ViewChild('topMasterKeySection') topMasterKeySection: ElementRef
  @ViewChild('keyWayFamily') keyWayFamily: ElementRef
  @ViewChild('combinatingRuleSetId') combinatingRuleSetId: ElementRef
  @ViewChild('chamberCount') chamberCount: ElementRef
  @ViewChild('status') status: ElementRef
  @ViewChild('keyName') keyName: ElementRef
  dataSource: MatTableDataSource<Structure> = new MatTableDataSource<Structure>([])
  displayedColumns: string[] = [
    'jobName',
    'structureNumber',
    'city',
    'state',
    'country',
    'topMasterKeyName',
    'topMasterKeySection',
    'keyWayFamily',
    'chamberCount',
    'status',
    'keyName',
    'more',
  ]

  displayedColumnFilters: string[] = [
    'jobName-filter',
    'structureNumber-filter',
    'city-filter',
    'state-filter',
    'country-filter',
    'topMasterKeyName-filter',
    'topMasterKeySection-filter',
    'keyWayFamily-filter',
    'chamberCount-filter',
    'status-filter',
    'keyName-filter',
    'more-filter',
  ]

  destroy$ = new Subject()
  search$ = new Subject<any>()
  loading: Loading

  keyingDisplayColumns = keyingDisplayColumns
  filteredStructures = new BehaviorSubject<Array<any>>([])
  library = new Library()
  structures
  users: User[]
  isSalesUser: boolean = false
  keyingDataSets

  advancedFilters = [
    {
      control: 'orderNumber',
      label: 'Order Number',
    },
    {
      control: 'registerNumber',
      label: 'Registry Number',
    },
    {
      control: 'combination',
      label: 'Combination',
    },
    {
      control: 'keySection',
      label: 'Key Way',
    },
    {
      control: 'note',
      label: 'Note',
      maxLength: 2000,
    },
  ]

  showAdvancedFilter = false

  filterForm: FormGroup

  navButtons = [{ name: 'Structures', active: true, icon: 'home', router: AppRoutingConfig.structuresUrl }]

  public tabs = []
  public selectedTab = this.tabs[0]
  public selectedTabIndex = 0

  isSearching = false
  sortBy = null
  isAsc = null
  isLoading = false
  initialLoad = true
  totalRows = 0
  pageSize = 25
  currentPage = 0
  searchFocus
  pageSizeOptions: number[] = [10, 25, 100]

  reports: any[] = [
    { name: 'Overview (internal only)', id: 1, status: null, alwaysShow: true, reportTypeDescription: null },
    { name: 'Bittings Report', id: 2, status: null, alwaysShow: false, reportTypeDescription: null },
    { name: 'Pinning Chart Report', id: 5, status: null, alwaysShow: false, reportTypeDescription: null },
    { name: 'Pinning Chart SL Report', id: 6, status: null, alwaysShow: false, reportTypeDescription: null },
    { name: 'Canada Report', id: 3, status: null, alwaysShow: false, reportTypeDescription: null },
    { name: 'Key System Design Report', id: 4, status: null, alwaysShow: false, reportTypeDescription: null },
    { name: 'Site Master Export', id: 7, status: null, alwaysShow: false, reportTypeDescription: null },
  ]

  allReports = []

  activeAdvancedFilters = 0

  todo: Structure[] = []
  doing: Structure[] = []
  mecs: Structure[] = []
  done: Structure[] = []

  // Report polling configuration and state management
  private readonly POLLING_INTERVAL_SECONDS = 3
  private readonly REPORT_STATUS = {
    QUEUED: 1,
    IN_PROGRESS: 2,
    COMPLETED: 3,
    ERROR: 4
  } as const

  private readonly REPORT_TYPE_IDS = {
    OVERVIEW: 1,
    BITTINGS: 2,
    CANADA: 3,
    KEY_SYSTEM_DESIGN: 4,
    PINNING_CHART: 5,
    PINNING_CHART_SL: 6,
    SITE_MASTER: 7,
    PARTIAL_BITTINGS: 9,
    PARTIAL_PINNING_CHART: 10,
    PARTIAL_PINNING_CHART_SL: 11
  } as const

  private apiSubscription: Subscription | null = null
  private structuresWithReportsInProgress: Map<number, { element: any, keySystemId: number }> = new Map()
  private structureReports: Map<number, any[]> = new Map()

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private masterKeyService: MasterKeyService,
    private navService: NavService,
    private dialog: MatDialog,
    private loadingService: LoadingService,
    private keyingAuthService: KeyingAuthService,
    private keyingDataSetsService: KeyingDataSetsService,
    private structureSearchParamsService: StructureSearchParamsService,
    public form: FormBuilder,
    private dialogService: DialogService,
    private clipboard: Clipboard,
    private errorService: ErrorService,
    private reportsService: ReportsService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    this.filterForm = this.form?.group({
      jobName: new FormControl(),
      structureNumber: new FormControl(),
      city: new FormControl(),
      state: new FormControl(),
      country: new FormControl(),
      topMasterKeyName: new FormControl(),
      topMasterKeySection: new FormControl(),
      keyWayFamily: new FormControl(),
      combinatingRuleSetId: new FormControl(),
      chamberCount: new FormControl(),
      orderNumber: new FormControl(),
      registerNumber: new FormControl(),
      combination: new FormControl(),
      keySection: new FormControl(),
      status: new FormControl(),
      keyName: new FormControl(),
      note: new FormControl(),
    })
    this.router.navigate([AppRoutingConfig.structuresUrl])
  }

  ngOnInit(): void {
    this.navService.setEntities(this.navButtons)

    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
    })

    this.keyingAuthService.users$.pipe(takeUntil(this.destroy$)).subscribe((users: User[]) => {
      this.users = users
    })

    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user: User) => {
      this.isSalesUser = user.isSalesUser
      // used for testing
      // this.isSalesUser = true
    })

    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }
        this.keyingDataSets = keyingDataSets
      })

    this.filterForm.controls['keyName'].disable()

    this.search$
      .pipe(
        switchMap((params) => this.keyingStructureService.getUserStructureData(params)),
        takeUntil(this.destroy$)
      )
      .subscribe((structure) => {
        if (structure.items) {
          this.loadingService.set({ message: null, isLoading: false })
          this.structures = structure
          this.dataSource = new MatTableDataSource(structure.items)
          this.dataSource.sort = this.sort
          this.currentPage = structure.pageIndex - 1
          this.totalRows = structure.totalItems
        }
        setTimeout(() => {
          this.paginator.pageIndex = this.currentPage
          this.paginator.length = this.totalRows
          this.isSearching = false
        })
      })

    this.setUpTabs()
  }

  /* istanbul ignore next */
  ngAfterViewInit(): void {}

  /* istanbul ignore next */
  setUpTabs() {
    if (this.isSalesUser) {
      this.tabs = [{ name: 'Find Structures', id: 1 }]
      this.selectedTab = this.tabs[0]
      this.onChangeTab({ index: 0 })
      this.getStructureDetails(true)
    } else {
      this.tabs = [
        { name: 'My Queue', id: 0 },
        { name: 'Find Structures', id: 1 },
        { name: 'Find Orders', id: 2 },
      ]
      this.selectedTab = this.tabs[0]
    }
  }

  /* istanbul ignore next */
  pageChanged(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.currentPage = 0
      this.paginator.pageIndex = 0
    } else {
      this.currentPage = event.pageIndex
    }
    this.pageSize = event.pageSize
    this.getStructureDetails()
  }

  /* istanbul ignore next */
  setSearchString(filter) {
    if (this.searchFocus !== this.filterForm.controls[filter].value && !this.initialLoad) {
      this.currentPage = 0
      this.getStructureDetails()
    }
  }

  /* istanbul ignore next */
  setSearchFocus(filter) {
    this.searchFocus = this.filterForm.controls[filter].value
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
    this.stopPolling()
  }

  /* istanbul ignore next */
  getStructureDetails(initialLoad: boolean = false) {
    const queryParams = true ? this.structureSearchParamsService.params : ({} as StructureSearchParams)

    const params = this.filterForm.getRawValue()
    params.PageIndex = queryParams.pageIndex ?? this.currentPage + 1
    params.PageSize = queryParams.pageSize ?? this.pageSize
    this.pageSize = params.PageSize

    if (initialLoad) {
      this.loadingService.set({ message: 'Retrieving structure details', isLoading: false })

      params.jobName = queryParams.jobName ?? null
      this.filterForm.controls.jobName.setValue(queryParams.jobName)

      params.structureNumber = queryParams.structureNumber ?? null
      this.filterForm.controls.structureNumber.setValue(queryParams.structureNumber)

      params.city = queryParams.city ?? null
      this.filterForm.controls.city.setValue(queryParams.city)

      params.state = queryParams.stateCode ?? null
      this.filterForm.controls.state.setValue(queryParams.stateCode)

      params.country = queryParams.countryCode ?? null
      this.filterForm.controls.country.setValue(queryParams.countryCode)

      params.topMasterKeyName = queryParams.topMasterKeyName ?? null
      this.filterForm.controls.topMasterKeyName.setValue(queryParams.topMasterKeyName)

      params.topMasterKeySection = queryParams.topMasterKeySection ?? null
      this.filterForm.controls.topMasterKeySection.setValue(queryParams.topMasterKeySection)

      params.keyWayFamily = queryParams.keyWayFamily ?? null
      this.filterForm.controls.keyWayFamily.setValue(queryParams.keyWayFamily)

      params.combinatingRuleSetId = queryParams.combinatingRuleSetId ?? null
      this.filterForm.controls.combinatingRuleSetId.setValue(queryParams.combinatingRuleSetId)

      params.chamberCount = queryParams.chamberCount ?? null
      this.filterForm.controls.chamberCount.setValue(queryParams.chamberCount)

      params.orderNumber = queryParams.orderNumber ?? null
      this.filterForm.controls.orderNumber.setValue(queryParams.orderNumber)

      params.registerNumber = queryParams.registerNumber ?? null
      this.filterForm.controls.registerNumber.setValue(queryParams.registerNumber)

      params.combination = queryParams.combination ?? null
      this.filterForm.controls.combination.setValue(queryParams.combination)

      params.keySection = queryParams.keySection ?? null
      this.filterForm.controls.keySection.setValue(queryParams.keySection)

      params.status = queryParams.status ?? null
      this.filterForm.controls.status.setValue(queryParams.status)

      params.keyName = queryParams.keyName ?? null
      this.filterForm.controls.keyName.setValue(queryParams.keyName)

      params.note = queryParams.note ?? null
      this.filterForm.controls.note.setValue(queryParams.note)

      this.initialLoad = false
    }

    const statusDisplayValue = params.status
    if (params.status) {
      params.status = this.resolveStatusId(params.status)
    }

    this.countActiveAdvancedFilters(params)
    this.sanatizeParams(params)
    this.structureSearchParamsService.next({ ...params, status: statusDisplayValue }, this.route)

    this.isSearching = true
    this.search$.next(params)
  }

  /* istanbul ignore next */
  countActiveAdvancedFilters(params) {
    const activeList = []
    this.advancedFilters.forEach((advancedFilterName) => {
      if (params[advancedFilterName.control] != null && params[advancedFilterName.control] != '') {
        activeList.push(params[advancedFilterName.control])
      }
    })
    this.activeAdvancedFilters = activeList.length
    if (this.activeAdvancedFilters > 0) {
      this.showAdvancedFilter = true
    }
  }

  /* istanbul ignore next */
  clearSearch() {
    this.currentPage = 0
    this.activeAdvancedFilters = 0
    this.showAdvancedFilter = false
    this.filterForm.reset()
    this.getStructureDetails()
  }

  /* istanbul ignore next */
  sanatizeParams(params) {
    Object.keys(params).forEach((key) => {
      if (typeof params[key] == 'string') {
        params[key] = params[key].replaceAll('`', '')
        this.filterForm.controls[key].setValue(params[key])
      }
    })
  }

  selectStructure(structure: Structure) {
    this.keyingStructureService
      .getStructure(structure.id)
      .pipe(take(1))
      .subscribe((strut) => {
        this.keyingStructureService.selectStructure(strut)

        if (!structure.city) {
          const dialogRef = this.dialogService.open(NewStructureDialogComponent, structure)

          dialogRef.afterClosed().subscribe((structure) => {
            if (structure) {
              this.keyingStructureService.updateStructure(structure)
            }
          })
        } else {
          if (structure.keySystems.length === 1) {
            this.keyingSystemService.selectKeySystem(structure.keySystems[0])
            this.router.navigate([AppRoutingConfig.getStructureUrl(structure.id, structure.keySystems[0].id)])
          } else {
            this.router.navigate([AppRoutingConfig.getStructureUrlNew(structure.id)])
          }
        }
      })
  }

  /* istanbul ignore next */
  openDialog(): void {
    const dialogRef = this.dialog.open(NewStructureDialogComponent, {
      panelClass: 'phx-dialog-container',
    })

    dialogRef.afterClosed().subscribe((structure) => {
      if (structure) {
        this.createStructure(structure)
      }
    })
  }

  /* istanbul ignore next */
  navNewStructure() {
    this.router.navigate([AppRoutingConfig.newStructureUrl])
  }

  /* istanbul ignore next */
  createStructure(data) {
    this.keyingStructureService.createStructure(data)
  }

  /* istanbul ignore next */
  updateSearch() {}

  // onChangeTab also sets up the filters and their listeners
  /* istanbul ignore next */
  onChangeTab(event: any) {
    this.selectedTabIndex = event.index

    this.selectedTab = this.tabs[this.selectedTabIndex]
    if (this.selectedTab.id === 1) {
      this.advancedFilters.forEach((advancedFilterName) => {
        this.filterForm
          .get(advancedFilterName.control)
          .valueChanges.pipe(
            debounceTime(600),
            distinctUntilChanged(),
            tap(() => {
              this.setSearchString(advancedFilterName.control)
            })
          )
          .subscribe()
      })

      this.getStructureDetails(true)

      setTimeout(() => {
        this.displayedColumns
          .filter((c) => c != 'more')
          .forEach((col) => {
            fromEvent(this[col].nativeElement, 'keyup')
              .pipe(
                debounceTime(600),
                distinctUntilChanged(),
                tap(() => {
                  this.setSearchString(col)
                })
              )
              .subscribe()
          })
      }, 1000)
    }
  }

  /* istanbul ignore next */
  getStatusString(status: number) {
    return KeySystemStatuses[status]
  }

  resolveStatusId(input: string): number | null {
    if (!input?.trim()) return null
    const match = Object.entries(KeySystemStatuses).find(([, label]) =>
      label.toLowerCase().includes(input.toLowerCase())
    )
    return match ? Number(match[0]) : null
  }

  copyStructureNumber(element) {
    const elementOrgId = element.structureNumber
    this.clipboard.copy(elementOrgId)
  }

  /**
   * Creates a default array of report configurations
   */
  private createDefaultReports(): any[] {
    return [
      { name: 'Overview (internal only)', id: this.REPORT_TYPE_IDS.OVERVIEW, status: null, alwaysShow: true, reportTypeDescription: null },
      { name: 'Bittings Report', id: this.REPORT_TYPE_IDS.BITTINGS, status: null, alwaysShow: false, reportTypeDescription: null },
      { name: 'Pinning Chart Report', id: this.REPORT_TYPE_IDS.PINNING_CHART, status: null, alwaysShow: false, reportTypeDescription: null },
      { name: 'Pinning Chart SL Report', id: this.REPORT_TYPE_IDS.PINNING_CHART_SL, status: null, alwaysShow: false, reportTypeDescription: null },
      { name: 'Canada Report', id: this.REPORT_TYPE_IDS.CANADA, status: null, alwaysShow: false, reportTypeDescription: null },
      { name: 'Key System Design Report', id: this.REPORT_TYPE_IDS.KEY_SYSTEM_DESIGN, status: null, alwaysShow: false, reportTypeDescription: null },
      { name: 'Site Master Export', id: this.REPORT_TYPE_IDS.SITE_MASTER, status: null, alwaysShow: false, reportTypeDescription: null },
    ]
  }

  /**
   * Maps full report type ID to its corresponding partial report type ID
   */
  private getPartialReportId(fullReportId: number): number | null {
    const mapping = {
      [this.REPORT_TYPE_IDS.BITTINGS]: this.REPORT_TYPE_IDS.PARTIAL_BITTINGS,
      [this.REPORT_TYPE_IDS.PINNING_CHART]: this.REPORT_TYPE_IDS.PARTIAL_PINNING_CHART,
      [this.REPORT_TYPE_IDS.PINNING_CHART_SL]: this.REPORT_TYPE_IDS.PARTIAL_PINNING_CHART_SL
    }
    return mapping[fullReportId] || null
  }

  /**
   * Checks if a report status indicates it is currently in progress
   */
  private isReportInProgress(statusId: number): boolean {
    return statusId === this.REPORT_STATUS.QUEUED || statusId === this.REPORT_STATUS.IN_PROGRESS
  }

  /**
   * Updates report status and description from API response
   * Returns true if any report is in progress
   */
  private updateReportsFromResponse(reports: any[], reportResponse: any[]): boolean {
    let hasReportInProgress = false

    reports.forEach((report) => {
      let existingReport = reportResponse.find(x => x.reportTypeId === report.id)

      // Check for partial report if full report not found
      if (!existingReport) {
        const partialReportId = this.getPartialReportId(report.id)
        if (partialReportId) {
          existingReport = reportResponse.find(x => x.reportTypeId === partialReportId)
        }
      }

      if (existingReport) {
        report.status = existingReport.statusId
        report.reportTypeDescription = this.getReportTypeDescription(existingReport)

        if (this.isReportInProgress(existingReport.statusId)) {
          hasReportInProgress = true
        }
      }
    })

    return hasReportInProgress
  }

  /* istanbul ignore next */
  getReportStatus(element) {
    const keySystemId = element.keySystems?.[0]?.id
    if (!keySystemId) {
      return
    }

    const reports = this.createDefaultReports()

    this.reportsService.getReportStatus(element.id, keySystemId).subscribe((reportResponse: any) => {
      const hasReportInProgress = this.updateReportsFromResponse(reports, reportResponse)

      // Store reports for this structure
      this.structureReports.set(element.id, reports)
      this.reports = reports

      // Manage polling based on report status
      this.managePollingForStructure(element.id, { element, keySystemId }, hasReportInProgress)

      // Trigger change detection
      this.cdr.detectChanges()
    })
  }

  /**
   * Manages polling state for a structure based on whether it has reports in progress
   */
  private managePollingForStructure(
    structureId: number,
    structureData: { element: any, keySystemId: number },
    hasReportInProgress: boolean
  ): void {
    if (hasReportInProgress) {
      this.structuresWithReportsInProgress.set(structureId, structureData)
    } else {
      this.structuresWithReportsInProgress.delete(structureId)
    }
    this.shouldScheduleRefresh()
  }

  /* istanbul ignore next */
  getStructureReports(element): any[] {
    // Return a copy to ensure Angular detects changes
    const reports = this.structureReports.get(element.id) || this.reports
    return [...reports]
  }

  /**
   * Optimistically sets report status to "in progress" for immediate UI feedback
   * Called when user initiates a report generation
   */
  /* istanbul ignore next */
  setReportStatusInProgress(element: any, reportTypeId: number, keySystemId: number): void {
    const existingReports = this.structureReports.get(element.id)
    const reports = existingReports ? [...existingReports] : this.createDefaultReports()

    // Update the specific report to show as queued
    const reportToUpdate = reports.find(r => r.id === reportTypeId)
    if (reportToUpdate) {
      reportToUpdate.status = this.REPORT_STATUS.QUEUED
    }

    // Update storage and trigger UI refresh
    this.structureReports.set(element.id, reports)
    this.structuresWithReportsInProgress.set(element.id, { element, keySystemId })
    this.cdr.detectChanges()
  }

  /**
   * Returns human-readable description for report type
   */
  /* istanbul ignore next */
  getReportTypeDescription(report): string {
    if (report.statusId === null || report.statusId === undefined) {
      return '[N/A]'
    }

    const partialReportIds = [
      this.REPORT_TYPE_IDS.PARTIAL_BITTINGS,
      this.REPORT_TYPE_IDS.PARTIAL_PINNING_CHART,
      this.REPORT_TYPE_IDS.PARTIAL_PINNING_CHART_SL
    ]

    return partialReportIds.includes(report.reportTypeId) ? 'Partial Report' : 'Full Report'
  }

  getDownloadTooltip(report): string {
    let tooltip = 'This report can now be downloaded.'
    if (report.reportTypeDescription && report.reportTypeDescription !== '[N/A]') {
      tooltip = `${report.reportTypeDescription.toLowerCase()} can now be downloaded.`
    }
    return tooltip
  }

  /* istanbul ignore next */
  downloadReport(element, report): void {
    // let reportType = Accept.OverviewReport
    // var reportName = ReportName.OverviewReport
    // let displayReportName = 'Overview'

    const combinatingRuleSetName = this.keyingDataSets.combinatingRuleSets.find(
      (kwf) => kwf.id === element?.combinatingRuleSetId
    )

    this.reportsService
      .downloadReport(element.id, element.keySystems[0].id, report.id)
      .pipe(take(1))
      .subscribe({
        next: (file) => {
          const newBlob = new Blob([file], { type: file.type })
          if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
            return
          }

          const data = window.URL.createObjectURL(newBlob)
          let reportName
          const link = document.createElement('a')
          link.href = data
          // link.download = `${element.structureNumber}.${element.keySystems[0].topMasterKeyName}.${
          //   element.name
          // }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`

          if (report.id === 7) {
            reportName = `SM.${element.structureNumber}.${element.TopMasterKeyName}.${element.keywayFamily}.${
              combinatingRuleSetName.name
            }.PinCount(#${element.pinCount}).${moment().format('MMDDYY')}.${file.type.split('/')[1]}`
          } else {
            reportName = `${element.structureNumber}.${element.keySystems[0].topMasterKeyName}.${
              report.name
            }.${moment().format('MMDDYY')}.${file.type.split('/')[1]}`
          }

          link.download = reportName

          link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))

          setTimeout(() => {
            // For Firefox it is necessary to delay revoking the ObjectURL
            window.URL.revokeObjectURL(data)
            link.remove()
          }, 100)
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  generateReport(element, reportTypeId, isRequeue): void {
    if (!element) {
      this.errorService.throw(new Error('Invalid structure element'))
      return
    }

    const keySystemId = element.keySystems && element.keySystems.length > 0 ? element.keySystems[0].id : null

    if (!keySystemId) {
      this.errorService.throw(new Error('No key system found for this structure'))
      return
    }

    if (reportTypeId === 2 || reportTypeId === 5 || reportTypeId === 6) {
      if (isRequeue) {
        this.handleReportRequestRequeue(element, reportTypeId, keySystemId)
        return
      } else {
        this.showReportDialog(element, keySystemId, ReportsDialogComponent, { id: reportTypeId }, true)
        return
      }
    }

    let childHttpRoute = ''
    let acceptHeader

    if (reportTypeId === 1) {
      childHttpRoute = 'Overview'
      acceptHeader = Accept.OverviewReport
    } else if (reportTypeId === 3) {
      childHttpRoute = 'CanadaExport'
      acceptHeader = Accept.CanadaReport
    } else if (reportTypeId === 4) {
      childHttpRoute = 'KeySystemDesign'
      acceptHeader = Accept.DesignReport
    } else if (reportTypeId === 7) {
      childHttpRoute = 'SiteMaster'
      acceptHeader = Accept.SiteMasterReport
    }

    // Immediately show spinner for the report
    this.setReportStatusInProgress(element, reportTypeId, keySystemId)

    this.keyingStructureService
      .submitFullReportRequest(element.id, keySystemId, childHttpRoute, acceptHeader, isRequeue)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          // Track this structure as having a report in progress
          this.structuresWithReportsInProgress.set(element.id, { element, keySystemId })
          this.checkAllStructuresReportStatus()
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  showReportDialog(element, keySystemId: number, dialog, data?, autoFocus = false): void {
    // Open the dialog immediately for instant response
    const dialogRef = this.dialogService.open(dialog, data, autoFocus)

    if (!dialogRef) {
      return
    }

    // Load the key system and hierarchy in parallel (dialog will update when ready)
    forkJoin({
      keySystem: this.keyingSystemService.getKeySystem(element.id, keySystemId),
      hierarchy: this.masterKeyService.getHierarchyMasterKeys(element.id, keySystemId),
    }).subscribe(({ keySystem, hierarchy }) => {
      // Attach the hierarchy to the key system
      keySystem.masterKeysKeying = hierarchy
      this.keyingSystemService.selectKeySystem(keySystem)
    })

    // Handle dialog close
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.reportType) {
          this.handleReportRequest(element, keySystemId, result, false)
        }
      }
    })
  }

  /* istanbul ignore next */
  handleReportRequest(element: any, keySystemId: number, payload: any, isRequeue: boolean) {
    if (payload.reportTypeId === 2) {
      this.handleBittingListReportRequest(element, keySystemId, payload, isRequeue)
    } else if (payload.reportTypeId === 5) {
      this.handlePinningReportRequest(element, keySystemId, payload, isRequeue)
    } else if (payload.reportTypeId === 6) {
      this.handleShearlineReportRequest(element, keySystemId, payload, isRequeue)
    }
  }

  /* istanbul ignore next */
  handleReportRequestRequeue(element: any, reportTypeId: number, keySystemId: number) {
    const payload = { reportType: 'Full' }
    if (reportTypeId === 2) {
      this.handleBittingListReportRequest(element, keySystemId, payload, true)
    } else if (reportTypeId === 5) {
      this.handlePinningReportRequest(element, keySystemId, payload, true)
    } else if (reportTypeId === 6) {
      this.handleShearlineReportRequest(element, keySystemId, payload, true)
    }
  }

  /* istanbul ignore next */
  handleBittingListReportRequest(element: any, keySystemId: number, payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest(element, keySystemId, 'BittingList', Accept.BittingListFullReport, isRequeue, 2)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest(element, keySystemId, 'PartialBittingList', Accept.BittingListPartialReport, payload, 2)
    }
  }

  /* istanbul ignore next */
  handlePinningReportRequest(element: any, keySystemId: number, payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest(element, keySystemId, 'PinningChart', Accept.PinningReport, isRequeue, 5)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest(element, keySystemId, 'PartialPinningChart', Accept.PartialPinningReport, payload, 5)
    }
  }

  /* istanbul ignore next */
  handleShearlineReportRequest(element: any, keySystemId: number, payload: any, isRequeue: boolean) {
    if (payload.reportType === 'Full') {
      this.submitFullReportRequest(element, keySystemId, 'PinningChartSL', Accept.ShearlineReport, isRequeue, 6)
    } else if (payload.reportType === 'Partial') {
      this.submitPartialReportRequest(element, keySystemId, 'PartialPinningChartSL', Accept.PartialShearlineReport, payload, 6)
    }
  }

  /* istanbul ignore next */
  submitFullReportRequest(element: any, keySystemId: number, childHttpRoute: string, acceptHeader: string, isRequeue = false, reportTypeId?: number) {
    // Immediately show spinner for the report if reportTypeId is provided
    if (reportTypeId) {
      this.setReportStatusInProgress(element, reportTypeId, keySystemId)
    }

    this.keyingStructureService
      .submitFullReportRequest(element.id, keySystemId, childHttpRoute, acceptHeader, isRequeue)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          this.getReportStatus(element)
          // Track this structure as having a report in progress
          this.structuresWithReportsInProgress.set(element.id, { element, keySystemId })
          this.checkAllStructuresReportStatus()
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /* istanbul ignore next */
  submitPartialReportRequest(element: any, keySystemId: number, childHttpRoute: string, acceptHeader: string, payload: any, reportTypeId?: number) {
    // Immediately show spinner for the report if reportTypeId is provided
    if (reportTypeId) {
      this.setReportStatusInProgress(element, reportTypeId, keySystemId)
    }

    this.keyingStructureService
      .submitPartialReportRequest(element.id, keySystemId, childHttpRoute, acceptHeader, payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.notify(
            'Generating Report',
            'This report will take some time to build. Please come back later to download.'
          )
          setTimeout(() => {
            this.toastService.close()
          }, 8000)
          this.getReportStatus(element)
          // Track this structure as having a report in progress
          this.structuresWithReportsInProgress.set(element.id, { element, keySystemId })
          this.checkAllStructuresReportStatus()
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /**
   * Polls report status for all structures currently being tracked
   * Updates UI when reports complete or status changes
   */
  /* istanbul ignore next */
  private checkAllStructuresReportStatus(): void {
    if (this.structuresWithReportsInProgress.size === 0) {
      return
    }

    let completedRequests = 0
    const totalRequests = this.structuresWithReportsInProgress.size

    this.structuresWithReportsInProgress.forEach((structureData) => {
      const { element, keySystemId } = structureData

      this.reportsService.getReportStatus(element.id, keySystemId).subscribe((reportResponse: any) => {
        const reports = this.createDefaultReports()
        const hasReportInProgress = this.updateReportsFromResponse(reports, reportResponse)

        // Update the stored reports for this structure
        this.structureReports.set(element.id, reports)

        // Remove from tracking if no longer in progress
        if (!hasReportInProgress) {
          this.structuresWithReportsInProgress.delete(element.id)
        }

        // Trigger change detection after all requests complete
        completedRequests++
        if (completedRequests === totalRequests) {
          this.refreshUIAndSchedule()
        }
      })
    })
  }

  /**
   * Refreshes the UI and updates polling schedule
   */
  private refreshUIAndSchedule(): void {
    // Force table to recognize data changes
    if (this.dataSource?.data) {
      this.dataSource.data = [...this.dataSource.data]
    }

    this.cdr.detectChanges()
    this.shouldScheduleRefresh()
  }

  /**
   * Starts the polling interval to check report status periodically
   */
  /* istanbul ignore next */
  private scheduleRefreshReports(): void {
    const pollingInterval = interval(this.POLLING_INTERVAL_SECONDS * 1000)
    this.apiSubscription = pollingInterval
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkAllStructuresReportStatus())
  }

  /**
   * Manages polling lifecycle based on whether any structures have reports in progress
   * Starts polling if needed, stops if no longer needed
   */
  /* istanbul ignore next */
  private shouldScheduleRefresh(): void {
    const hasReportsInProgress = this.structuresWithReportsInProgress.size > 0

    if (!this.apiSubscription && hasReportsInProgress) {
      this.scheduleRefreshReports()
    } else if (this.apiSubscription && !hasReportsInProgress) {
      this.stopPolling()
    }
  }

  /**
   * Stops the polling interval and cleans up subscription
   */
  private stopPolling(): void {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe()
      this.apiSubscription = null
    }
  }
}
